const teamPageEN = {
	header: {
		title: "Team",
		subtitle: "Choose someone to edit their profile and the services they offer.",
		inviteStaff: "Invite staff",
		manageStaff: "Manage staff",
	},
	stats: {
		active: "Active",
		pending: "Pending",
		invited: "Invited",
	},
	ownerCalendar: {
		label: "Your calendar",
		on: "Customers can book appointments with you.",
		off: "You run the shop. Customers book your staff, not you.",
		turnOff: "I don't take appointments",
		turnOn: "I take appointments",
		working: "Updating…",
		pricingNote: "You're billed per calendar, and the first one is included in your plan.",
		onSuccess: "Done — your calendar is active. Set your hours from the Calendar tab.",
		offSuccess: "Done — you no longer appear as bookable.",
		cancelledSuccess: "Done — {{count}} appointment(s) cancelled and your calendar is off. We've emailed those customers.",
		failed: "We couldn't update your calendar. Please try again.",
		blocked: "You have {{count}} upcoming appointment(s) on your calendar. Reassign them from the Calendar tab, or cancel them, before you turn it off.",
		noCalendars: "Nobody in this shop takes bookings yet. Invite a staff member, or add your own calendar back.",

		/* The confirm step. Every line here is something the owner cannot see from
		   the card and would only discover afterwards — a deleted schedule, an email
		   already sent to a customer, an invoice line that moved. */
		confirmOffTitle: "Turn off your calendar?",
		confirmOnTitle: "Turn on your calendar?",
		confirmOffSchedule: "Your working hours, days off and one-off changes are deleted for good. Turn it back on later and you start from an empty calendar.",
		confirmOffBookings: "Any upcoming appointments on your calendar are cancelled. We'll tell you how many first, so you can go and reassign them before you decide.",
		confirmOffVisibility: "You disappear from your shop page. Customers won't be able to book you.",
		confirmOffNoCalendars: "Nobody in the shop takes bookings after this. Your shop stays listed, but nothing can be booked until a staff member is active, or you turn this back on.",
		confirmOnSchedule: "You start with an empty schedule. Customers can't book you until you set your hours in the Calendar tab.",
		confirmOnVisibility: "You'll appear on your shop page as someone customers can book.",
		billingUp: "One more billable calendar. It's added to your next invoice, not charged today.",
		billingDown: "One fewer billable calendar. The adjustment lands on your next invoice — there's no refund today.",
		billingIncluded: "No extra charge — your plan includes your shop's first calendar.",
		billingSame: "Your bill stays the same — the base plan covers your shop whether or not you take bookings.",
		billingCapped: "No change to your bill — your plan charges for at most {{max}} calendars, and you're already past that.",
		billingPending: "Nothing is charged today. The count is saved and applied once your subscription is active.",
		confirmOff: "Turn it off",
		confirmOn: "Turn it on",

		/* The cancel step — the only place in the app that calls off somebody
		   else's appointment because of a setting. It gets real numbers rather
		   than "any upcoming appointments", because by this point the server has
		   counted them and vagueness here is what makes a click regrettable. */
		cancelTitle: "Cancel {{count}} upcoming appointment(s)?",
		cancelWhat: "You have {{count}} appointment(s) still to come. Turning your calendar off calls them off for good — there's no undo, and getting one back means the customer books again from scratch.",
		cancelEmails: "We email every one of those customers to tell them it's off, so nobody turns up to a closed calendar.",
		cancelReassignInstead: "Want to keep them? Close this, reassign each one to a staff member from the Calendar tab, then turn your calendar off.",
		cancelConfirm: "Cancel them and turn it off",
		keepThem: "Keep them",
		cancel: "Cancel",
	},
	empty: {
		noStaff: "No one on your team yet",
		noStaffHint: "Invite your first staff member and they'll show up here.",
		selectTitle: "Select a team member",
		selectHint: "Pick someone from the roster to view and edit their profile.",
		noServicesHint: "Assign services so this person can start taking bookings.",
		notBookableTitle: "Not taking appointments",
		notBookableHint: "You run the shop, so you have no services or calendar. You can change this from Your calendar above.",
	},
	messages: {
		activateFailed: "We couldn't activate this profile. Please try again.",
		noValidEmail: "Please enter at least one valid email to continue.",
		inviteFailed: "We couldn't send some invites. Please try again.",
		removeFailed: "We couldn't remove this team member. Please try again.",
		removeSuccess: "Done — {{name}} is no longer part of your team.",
		promoteFailed: "We couldn't complete the promotion. Please try again.",
		demoteFailed: "We couldn't update the role. Please try again.",
		reinviteFailed: "We couldn't resend the invite. Please try again.",
		cancelInviteFailed: "We couldn't cancel the invite. Please try again.",
	},
	confirm: {
		removeStaff: "Remove this staff member?",
		popupTitle: "Remove {{name}}?",
		popupSubtitle: "This action cannot be undone.",
		cancelInviteTitle: "Cancel invite for {{name}}?",
		cancelInviteSubtitle: "The staff member will no longer be able to join.",
		cancel: "Cancel",
		confirm: "Confirm",
		warningBookings:
			"Please review this staff member’s upcoming bookings and reassign them if needed. If the staff member is removed, all active appointments in their calendar will be automatically canceled. Are you sure you want to proceed?",
	},
	status: {
		active: "Active",
		registered: "Registered",
		invited: "Invited",
		joined: "Registered",
		notBookable: "Not bookable",
	},
	main: {
		roster: "Roster",
		services: "Services",
		noServices: "No services assigned",
		launch: "Launch",
		active: "Active",
		inactive: "Inactive",
		homeService: "Home service",
		you: "You",
		profile: "Profile",
		serviceType: {
			home: "Home service",
			online: "Online",
		},
	},
	labels: {
		bio: "Bio",
		email: "Email:",
		phone: "Phone:",
		emailShort: "Email",
		phoneShort: "Phone",
		noPhone: "No phone added",
		firstName: "First name",
		lastName: "Last name",
		emailAddress: "Email address",
	},
	actions: {
		edit: "Edit",
		addStaff: "+ Add Staff",
		saveClose: "Save & Close",
		sending: "Sending...",
		removing: "Removing...",
		cancel: "Cancel",
		confirm: "Confirm",
		promoteToAdmin: "Promote to Admin",
		demoteToStaff: "Demote to Staff",
		reinvite: "Re-invite",
		cancelInvite: "Cancel Invite",
		removeStaff: "Remove",
	},

	role: {
		owner: "Owner",
		admin: "Admin",
	},

	toast: {
		promotedToAdmin: "You're all set! {{name}} is now an admin.",
		demotedToStaff: "{{name}}'s role was updated to staff.",
		inviteResent: "All set! We resent the invite.",
		inviteCancelled: "We canceled the invite for {{name}}.",
		inviteSent:
			"Invite sent! The staff member will get an email to finish setting up their profile. They'll show as active on your business once they're done.",
	},

	editProfile: "Edit profile",

	modal: {
		team: {
			description: "Invite people, set who can administer, and remove anyone who has left.",
			newInvite: "New invite",
			newInviteHint: "They'll get an email to join your team.",
		},
		info: {
			description: "Name and bio as customers will see them.",
		},
		services: {
			description: "Tick everything this person is trained to do.",
		},
		photo: {
			title: "Profile photo",
			description: "A clear headshot helps customers recognise who they're booking.",
			choose: "Choose image",
			change: "Choose another",
			save: "Save photo",
			uploading: "Uploading...",
			none: "No image selected",
			invalid: "That file isn't a valid image. Please choose a photo.",
			failed: "We couldn't upload the image. Please try again.",
			pickFirst: "Please choose an image first.",
		},
	},
};
export default teamPageEN;
